import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import { Banner, Loading, Empty, Modal, SectionIntro, errorMessage } from '../components/ui';
import './Commodities.css';

const BLANK = { name: '', regulationText: '', matchAliases: '', sortOrder: 0 };

function Commodities() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [form, setForm] = useState(BLANK);
  const [preview, setPreview] = useState(null);
  const [testSelection, setTestSelection] = useState('');

  let user = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch { user = {}; }
  const isSuperAdmin = user.role === 'Super Admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/commodities`, { params: { includeInactive: true } });
      setRows(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load commodities.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(BLANK); setEditing('new'); };

  const openEdit = (row) => {
    setForm({
      name: row.Name,
      regulationText: row.RegulationText,
      matchAliases: row.MatchAliases || '',
      sortOrder: row.SortOrder ?? 0,
    });
    setEditing(row);
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (editing === 'new') {
        await axios.post(`${API_BASE}/api/commodities`, form);
        setSuccess(`"${form.name}" added.`);
      } else {
        await axios.put(`${API_BASE}/api/commodities/${editing.Id}`, form);
        setSuccess(`"${form.name}" updated.`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    setError('');
    try {
      await axios.put(`${API_BASE}/api/commodities/${row.Id}`, { isActive: !row.IsActive });
      setSuccess(`"${row.Name}" ${row.IsActive ? 'deactivated' : 'reactivated'}.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove "${row.Name}"?`)) return;
    setError('');
    try {
      const res = await axios.delete(`${API_BASE}/api/commodities/${row.Id}`);
      setSuccess(res.data.message);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const runPreview = async () => {
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/api/commodities/preview`, { rawSelection: testSelection });
      setPreview(res.data);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Commodities</h2>
            <p className="page-subtitle">
              The certificate types an applicant can select, each with the regulation wording
              printed on their Certificate of Good Standing. Add a commodity here and it becomes
              available immediately — no code change needed.
            </p>
          </div>
          {isSuperAdmin && (
            <div className="page-header-actions">
              <button className="btn btn-primary" onClick={openNew}>Add commodity</button>
            </div>
          )}
        </div>

        <Banner kind="success" message={success} onDismiss={() => setSuccess('')} />
        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        {!isSuperAdmin && (
          <div className="callout callout-note">
            <span className="callout-label">Read only</span>
            Only a Super Admin can add or change commodities.
          </div>
        )}

        {loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty title="No commodities configured." hint="Run npm run seed, or add them here." />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="nowrap">Order</th>
                  <th>Commodity</th>
                  <th>Regulation wording printed on the certificate</th>
                  <th>Also matches</th>
                  <th className="nowrap">Used by</th>
                  <th className="nowrap">Status</th>
                  {isSuperAdmin && <th className="nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.Id} className={row.IsActive ? '' : 'comm-inactive'}>
                    <td className="numeric nowrap">{row.SortOrder}</td>
                    <td><strong>{row.Name}</strong></td>
                    <td className="comm-reg">{row.RegulationText}</td>
                    <td className="comm-aliases">
                      {(row.MatchAliases || '').split('|').filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="numeric nowrap">{row.UsageCount || 0}</td>
                    <td className="nowrap">
                      <span className={`badge ${row.IsActive ? 'badge-green' : 'badge-grey'}`}>
                        {row.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="nowrap">
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(row)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(row)}>
                          {row.IsActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                        <button className="btn btn-ghost btn-sm comm-remove" onClick={() => remove(row)}>Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Wording tester */}
      <div className="page-card">
        <h3 className="comm-section-title">Test the certificate wording</h3>
        <SectionIntro>
          Paste a "Certificate of Good Standing" answer exactly as it appears in the sheet to see
          which commodities it resolves to and the exact text that lands on the certificate.
        </SectionIntro>

        <div className="field">
          <label htmlFor="comm-test">Applicant's selection</label>
          <input
            id="comm-test"
            type="text"
            value={testSelection}
            onChange={(e) => setTestSelection(e.target.value)}
            placeholder="Poultry Meat Certificate, Processed Meat Products Certificate"
          />
        </div>

        <button className="btn btn-secondary" onClick={runPreview} disabled={!testSelection.trim()}>
          Preview wording
        </button>

        {preview && (
          <div className="comm-preview">
            <div className="detail-rows">
              <div className="detail-row">
                <span className="detail-label">Matched</span>
                <span className="detail-value">
                  {preview.matched.length ? preview.matched.join(', ') : '—'}
                </span>
              </div>
              {preview.unmatched.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Not recognised</span>
                  <span className="detail-value comm-unmatched">{preview.unmatched.join(', ')}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Match method</span>
                <span className="detail-value">{preview.matchMethod}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Letters' Commodity field</span>
                <span className="detail-value">{preview.commodityField || '—'}</span>
              </div>
            </div>

            <div className="comm-cert-preview">
              <span className="comm-cert-label">Certificate Regulation block</span>
              <pre>{preview.regulation}</pre>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? 'Add a commodity' : `Edit ${editing.Name}`}
          subtitle="The regulation wording is appended to the certificate's Regulation block, after the standard preamble."
          onClose={() => setEditing(null)}
        >
          <div className="field">
            <label className="field-required" htmlFor="comm-name">Commodity name</label>
            <input
              id="comm-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Poultry Meat Certificate"
            />
            <span className="field-hint">
              Must match the Google Form option exactly, so submissions resolve automatically.
            </span>
          </div>

          <div className="field">
            <label className="field-required" htmlFor="comm-reg">Regulation wording</label>
            <textarea
              id="comm-reg"
              value={form.regulationText}
              onChange={(e) => setForm({ ...form, regulationText: e.target.value })}
              placeholder="& R.1283 of 4 October 2019"
            />
            <span className="field-hint">
              Start with "&amp;" so it reads correctly when several commodities are combined.
            </span>
          </div>

          <div className="field">
            <label htmlFor="comm-alias">Also matches (optional)</label>
            <input
              id="comm-alias"
              type="text"
              value={form.matchAliases}
              onChange={(e) => setForm({ ...form, matchAliases: e.target.value })}
              placeholder="Poultry Meat|Poultry"
            />
            <span className="field-hint">
              Alternative spellings, separated by a vertical bar. Useful when the form wording
              changes but old responses must still resolve.
            </span>
          </div>

          <div className="field">
            <label htmlFor="comm-order">Display order</label>
            <input
              id="comm-order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            />
            <span className="field-hint">
              Also the order regulation references appear on a multi-commodity certificate.
            </span>
          </div>

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={busy || !form.name.trim() || !form.regulationText.trim()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Commodities;
